import React, { useState, useCallback } from "react";
import useGenerateGreetMutation from "../hooks/useGenerateGreetMutation";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButtom";
import DSTextField from "../../../system-design/DSTextField";
import DSDialogActions from "../../../system-design/DSDialogActions";
import { DSTypography } from "../../../system-design/Typography";
import GreetingPreview from "./GreetingPreview";
import ToneSelector, { GreetingTone } from "./ToneSelector";
import DSBox from "../../../system-design/DSBox";
import DSButton from "../../../system-design/DSButtom";
import { KLYDO_BLACK, KLYDO_WHITE } from "../../../../consts/colors";
import { useTheme } from "@mui/material";

type GenerateGreetModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (greet: string) => void;
};

const GenerateGreetDialog: React.FC<GenerateGreetModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [recipientName, setRecipientName] = useState<string>("");
  const [eventDescription, setEventDescription] = useState<string>("");
  const [selectedTone, setSelectedTone] = useState<GreetingTone>(null);
  const { palette } = useTheme();
  const [generatedGreet, setGeneratedGreet] = useState<string>("");

  const {
    mutate: generateGreetMutation,
    data: greet,
    isPending,
    isError,
  } = useGenerateGreetMutation({
    onSuccess: (data) => {
      setGeneratedGreet(data);
    },
  });

  const clearState = () => {
    setGeneratedGreet("");
    setRecipientName("");
    setEventDescription("");
    setSelectedTone(null);
  };
  const handleSave = () => {
    onSave(generatedGreet);
    clearState();
  };

  const handleCancel = () => {
    clearState();
    onClose();
  };

  const handleGenerateGreet = () => {
    generateGreetMutation({
      recipientName,
      eventDescription,
      tone: selectedTone,
    });
  };

  const handleRecipientNameChange = useCallback((value: string) => {
    setRecipientName(value);
  }, []);

  const handleEventDescriptionChange = useCallback((value: string) => {
    setEventDescription(value);
  }, []);

  const handleToneChange = useCallback((tone: GreetingTone) => {
    setSelectedTone(tone);
  }, []);

  return (
    <DSDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DSDialogTitle>Generate Greeting</DSDialogTitle>
      <DSDialogContent>
        <DSTextField
          fullWidth
          label="Recipient Name"
          margin="dense"
          value={recipientName}
          onChange={(e) => handleRecipientNameChange(e.target.value)}
        />
        <DSTextField
          fullWidth
          label="Event Description"
          margin="dense"
          value={eventDescription}
          onChange={(e) => handleEventDescriptionChange(e.target.value)}
        />
        <ToneSelector value={selectedTone} onChange={handleToneChange} />
        <DSBox sx={{ pt: 2, display: "flex", justifyContent: "space-between" }}>
          <DSTypography>Greeting Preview:</DSTypography>
          <DSButton
            onClick={handleGenerateGreet}
            disabled={!recipientName || !eventDescription || isPending}
            sx={{
              background: KLYDO_WHITE,
              color: KLYDO_BLACK,
              borderRadius: 70,
              padding: "4px 12px",
              fontFamily: "Gilroy-Bold",
              "&.Mui-disabled": {
                backgroundColor: palette.action.disabledBackground,
                color: palette.action.disabled,
              },
            }}
          >
            Generate
          </DSButton>
        </DSBox>

        <GreetingPreview
          greet={greet}
          isLoading={isPending}
          isError={isError}
        />
      </DSDialogContent>

      <DSDialogActions>
        <DSSecondaryButton
          sx={{
            color: palette.text.primary,
          }}
          onClick={handleCancel}
        >
          Cancel
        </DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave} disabled={!generatedGreet}>
          Save
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default GenerateGreetDialog;
